import React, { useMemo } from 'react';
import {
  Button, ButtonColor, ButtonSize, ButtonVariant, FontWeight, ICON_MAP_MARKER, ICON_PHONE, Maps, Spacing, SpacingName, Typography, TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import { formatOpeningHours } from '@doveit/hammer';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout, { PageLayoutSize } from '../client/layouts/page-layout/PageLayout';
import { template } from '../client/utils/string/string';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import * as domain from '../domain/graphcms';
import { REAL_ESTATE_AGENCY_BY_SLUG } from '../client/graphql/queries/realEstateAgencies';
import {
  buildAgenciesURLObject,
  buildAgencyByLocalityURLObject,
  buildAgentURLObject,
  buildListingURLObject,
  buildRealEstateAgencyURLObject,
  buildSellPropertyURLObject,
  goToPage,
  relativeToAbsoluteURL,
} from '../client/utils/links';
import { SortOrder } from '../domain/pagination';
import { Status } from '../domain/status';
import { getLogger } from '../logger/isomorphic';
import { PropertySearchResult } from '../services/dtos/property';
import PageSection from '../client/components/page-section/PageSection';
import PropertySearchResultCardList from '../client/components/property-search-result-card-list/PropertySearchResultCardList';
import Intro from '../client/components/intro-V2/Intro';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { CTA, TextAlign } from '../client/types';
import { PropertiesResponse, getAll } from '../services/properties/properties';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';
import CardsHList from '../client/components/cards-hlist/CardsHList';
import SoldPropertyCard from '../client/components/sold-property-card/SoldPropertyCard';
import { sortAgents } from '../services/agents/sortAgents';
import withNextConfig from '../client/hocs/withNextConfig/withNextConfig';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import { Lead } from '../services/dtos';
import { getRealEstateAgencyStructuredData } from '../data/structured/realEstateAgency';
import { NullPointerError } from '../client/utils/error/error';
import {
  ReviewsSection, ReviewsSectionProps, TeamSection, TeamSectionProps,
} from '../client/sections';
import variables from '../client/themes/variables';

export const LEAD_SOURCE_PERSONAL = 'personal';

const SOLD_PROPERTY_RESULT_SIZE = 3;

const EMPTY_PROPERTY_RESPONSE = {
  content: [],
  totalElements: 0,
} as unknown as PropertiesResponse;

export interface RealEstateAgencyProps {
  SEO: PageHeadProps['SEO'],
  realEstateAgency: domain.RealEstateAgency,
  intro: {
    title: string,
  },
  teamSection: TeamSectionProps,
  sellProperty: {
    title: string,
    cta: CTA,
  },
  latestPropertiesSection: {
    title: string,
    items: PropertySearchResult[],
  },
  soldPropertiesSection: {
    title: string,
    items: PropertySearchResult[],
  },
  reviewsSection: ReviewsSectionProps | null,
  form: {
    title: string,
    intro: string,
    label: string,
  },
  config: {
    MAPS: {
      apiKey: string,
    },
  },
}

const logger = getLogger();

export const RealEstateAgency: NextPage<RealEstateAgencyProps> = ({
  SEO,
  intro,
  realEstateAgency,
  sellProperty,
  latestPropertiesSection,
  soldPropertiesSection,
  reviewsSection,
  teamSection,
  form,
  config,
}) => {
  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [
    {
      label: 'Agenzie immobiliari',
      urlObject: buildAgenciesURLObject(),
    },
    {
      label: realEstateAgency.parent.locality.name,
      urlObject: buildAgencyByLocalityURLObject({
        locality: realEstateAgency.parent.locality.slug,
      }),
    },
    {
      label: realEstateAgency.name,
      urlObject: buildRealEstateAgencyURLObject({
        locality: realEstateAgency.parent.locality.slug,
        slug: realEstateAgency.slug,
      }),
    },
  ], [realEstateAgency]);

  const sellPropertyUrl = buildSellPropertyURLObject();

  const onCreateLeadSuccess = React.useCallback((lead: Lead) => {
    sendLeadSubmitEventAndRedirect('main', lead);
  }, []);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        type="article"
        structuredData={
          getStructuredData(
            getBreadcrumbStructuredData(breadcrumb),
            realEstateAgency.address
              ? getRealEstateAgencyStructuredData(
                realEstateAgency.name,
                realEstateAgency.address.locality,
                {
                  ...realEstateAgency.address,
                  url: SEO.canonical,
                },
                realEstateAgency.phoneNumber ?? '+39 327 2074613',
                realEstateAgency.openingHours,
              )
              : undefined,
          )
        }
      />
      <PageLayout>
        <PageLayout.Wrapper maxWidth={PageLayoutSize.L}>
          <PageLayout.Breadcrumb items={breadcrumb} />
          <PageSection padding={[0, 0, SpacingName.BIGGER]}>
            <Intro
              title={intro.title}
              description={realEstateAgency.info}
              rightSlot={(
                <>
                  <Maps
                    apiKey={config?.MAPS?.apiKey}
                    title="Posizione agenzia"
                    params={`${realEstateAgency.address.streetAddress},${realEstateAgency.address.locality}`}
                    width="100%"
                    height="300px"
                  />

                  <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                    <Button
                      href={`http://www.google.com/maps?q=${realEstateAgency.address.streetAddress},${realEstateAgency.address.locality}`}
                      label={`${realEstateAgency.address.streetAddress} - ${realEstateAgency.address.locality}`}
                      variant={ButtonVariant.OUTLINE}
                      color={ButtonColor.NEUTRAL}
                      size={ButtonSize.SMALL}
                      icon={ICON_MAP_MARKER}
                      target="_blank"
                      rounded
                    />
                  </Spacing>
                  {realEstateAgency.phoneNumber && (
                    <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                      <Button
                        href={`tel:${realEstateAgency.phoneNumber}`}
                        label={realEstateAgency.phoneNumber}
                        variant={ButtonVariant.OUTLINE}
                        size={ButtonSize.SMALL}
                        icon={ICON_PHONE}
                        rounded
                      />
                    </Spacing>
                  )}
                  {realEstateAgency.openingHours && (
                    <Spacing margin={[SpacingName.SMALL, 0, 0]}>
                      <Typography.BODY tagName="div" fontWeight={FontWeight.BOLD}>
                        Orari
                      </Typography.BODY>
                      {formatOpeningHours(realEstateAgency.openingHours, true).map((openingSlot) => (
                        <Spacing
                          margin={[SpacingName.SMALLEST, 0, 0]}
                          key={openingSlot}
                        >
                          <Typography.BODY>
                            {openingSlot}
                          </Typography.BODY>
                        </Spacing>
                      ))}
                    </Spacing>
                  )}
                </>
              )}
            />
          </PageSection>
        </PageLayout.Wrapper>

        <PageLayout.Content>
          <PageLayout.Main>
            <TeamSection
              {...teamSection}
              backgroundGradient={variables.sectionBackgroundGradient}
            />

            {latestPropertiesSection?.items.length > 0 && (
              <PageSection
                padding={[SpacingName.BIGGER, 0]}
                data-ref="latest-properties-section"
              >
                <PageSection.Wrapper>
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={latestPropertiesSection.title}
                  />
                </PageSection.Wrapper>
                <PageSection.Wrapper withoutPad>
                  <PageSection.Block>
                    <PropertySearchResultCardList
                      inline={3}
                      items={latestPropertiesSection.items.map((searchResult) => ({
                        searchResult,
                        urlObject: buildListingURLObject(searchResult),
                        onClick: () => goToPage(buildListingURLObject(searchResult)),
                      }))}
                    />
                  </PageSection.Block>
                </PageSection.Wrapper>
              </PageSection>
            )}

            {soldPropertiesSection.items.length > 0 && (
              <PageSection
                padding={[SpacingName.BIGGER, 0]}
                data-ref="sold-properties-section"
              >
                <PageSection.Wrapper>
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={soldPropertiesSection.title}
                  />
                </PageSection.Wrapper>
                <PageSection.Wrapper withoutPad>
                  <PageSection.Block>
                    <CardsHList
                      inline={3}
                      sideSpacing={SpacingName.SMALL}
                    >
                      {soldPropertiesSection.items.map((searchResult) => (
                        <CardsHList.Item key={searchResult.id}>
                          <SoldPropertyCard
                            title={`${searchResult.locality}${(searchResult.suburb ? ` - ${searchResult.suburb}` : '')}`}
                            saving={searchResult.savingLabel}
                            image={searchResult.coverImageSet.small}
                            banner="Venduto"
                          />
                        </CardsHList.Item>
                      ))}
                    </CardsHList>
                  </PageSection.Block>
                </PageSection.Wrapper>
              </PageSection>
            )}

            {reviewsSection && (<ReviewsSection {...reviewsSection} />)}

            <PageSection
              backgroundColor="neutrals.lighter"
              padding={[SpacingName.BIGGER, 0]}
            >
              <PageSection.Wrapper>
                <PageSection.Title
                  size={TypographyName.H2}
                  color="brand.primary"
                  content={sellProperty.title}
                />
                <PageSection.Block>
                  <ArticleMarkdown>
                    {realEstateAgency.evaluation}
                  </ArticleMarkdown>
                </PageSection.Block>
                <PageSection.Actions>
                  <Button
                    label={sellProperty.cta.label}
                    href={sellPropertyUrl.as}
                    onClick={() => goToPage(sellPropertyUrl)}
                    dataRef={sellProperty.cta.dataRef}
                    dataTracking={sellProperty.cta.dataTracking}
                  />
                </PageSection.Actions>
              </PageSection.Wrapper>
            </PageSection>

            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={form.title}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Description
                  text={form.intro}
                  textAlign={TextAlign.CENTER}
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
                <CreateLeadSection
                  title={form.label}
                  source={realEstateAgency.franchising
                    ? LEAD_SOURCE_PERSONAL
                    : undefined}
                  campaign={realEstateAgency.franchising
                    ? (realEstateAgency.agents.find(({ jobTitleType }) => jobTitleType.name.toLowerCase() === 'broker')?.slug || realEstateAgency.slug)
                    : realEstateAgency.slug}
                  dataRef="lead-form-main"
                  onSuccess={onCreateLeadSuccess}
                  dataTracking="lead-form-main-submit"
                />
              </PageSection.Wrapper>
            </PageSection>

          </PageLayout.Main>
        </PageLayout.Content>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<RealEstateAgencyProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const { slug, locality } = query;
  const [
    pageData,
    { data },
  ] = await Promise.all([
    importDynamicData('pages/real-estate-agency.yml'),
    graphQLClient.query<{ realEstateAgency: domain.RealEstateAgency, reviews: domain.Review[] }>({
      query: REAL_ESTATE_AGENCY_BY_SLUG,
      variables: {
        slug,
      },
    }),
  ]);

  const { realEstateAgency, reviews } = data;

  if (!realEstateAgency?.address || !realEstateAgency?.parent) {
    logger.error({
      err: new NullPointerError(`Cannot find a real estate agency with slug: ${slug} address ${realEstateAgency?.address} parent ${realEstateAgency?.parent}`),
    }, `Not valid real estate agency, redirect to agency page for ${locality}`);
    return {
      redirect: {
        destination: buildAgencyByLocalityURLObject({ locality: locality.toString() }).as,
        permanent: true,
      },
    };
  }

  let latestProvinceSoldProperties: PropertySearchResult[] = [];

  const teamEmails: string[] = realEstateAgency.agents.map((agent) => agent.email);
  const isAFranchisingAgency = realEstateAgency.franchising;

  const [{ content: latestTeamProperties }, { content: latestTeamSoldProperties }] = await Promise.all([
    getAll(
      {
        status: Status.LIVE,
        agentEmail: teamEmails,
      },
      {
        size: 9,
        sort: {
          published_at: SortOrder.DESC,
        },
      },
    ).catch((err) => {
      logger.warn({ err }, 'Error while retrieving properties by team emails');
      return EMPTY_PROPERTY_RESPONSE;
    }),
    getAll(
      {
        status: Status.VENDUTO,
        agentEmail: teamEmails,
      },
      {
        size: SOLD_PROPERTY_RESULT_SIZE,
        sort: {
          sold_at: SortOrder.DESC,
        },
      },
    ).catch((err) => {
      logger.warn({ err }, 'Error while retrieving sold properties by team emails');
      return EMPTY_PROPERTY_RESPONSE;
    }),
  ]);

  if (!isAFranchisingAgency && latestTeamSoldProperties.length < SOLD_PROPERTY_RESULT_SIZE) {
    latestProvinceSoldProperties = (await getAll({
      status: Status.VENDUTO,
      province: realEstateAgency.parent.locality.province.slug,
      exclude: latestTeamSoldProperties.map((property) => property.id),
    }, {
      size: SOLD_PROPERTY_RESULT_SIZE,
      sort: {
        sold_at: SortOrder.DESC,
      },
    }).catch((err) => {
      logger.warn({ err }, 'Error while retrieving latest province sold properties');
      return EMPTY_PROPERTY_RESPONSE;
    })).content;
  }

  const actual = buildRealEstateAgencyURLObject({
    locality: locality as string,
    slug: slug as string,
  });

  const agencyURLObject = buildRealEstateAgencyURLObject({
    locality: realEstateAgency.parent.locality.slug,
    slug: slug as string,
  });

  if (actual.as !== agencyURLObject.as) {
    return {
      redirect: {
        destination: agencyURLObject.as,
        permanent: false,
      },
    };
  }

  const {
    SEO,
    sellProperty,
    latestPropertiesSection,
    soldPropertiesSection,
    intro,
    team,
    form,
  } = template(pageData, {
    locality: realEstateAgency.address.locality,
    name: realEstateAgency.name,
  });

  const soldProperties: PropertySearchResult[] = [...latestTeamSoldProperties, ...latestProvinceSoldProperties].slice(0, SOLD_PROPERTY_RESULT_SIZE);

  return {
    props: {
      SEO: {
        ...SEO,
        canonical: relativeToAbsoluteURL(agencyURLObject.as),
      },
      realEstateAgency,
      sellProperty,
      latestPropertiesSection: {
        ...latestPropertiesSection,
        items: latestTeamProperties,
      },
      soldPropertiesSection: {
        ...soldPropertiesSection,
        items: soldProperties,
      },
      reviewsSection: reviews?.length > 0 ? {
        reviews,
        title: 'Facciamo felici i clienti. Leggi le loro recensioni.',
        trustPilot: {
          label: 'Lascia una recensione',
          href: 'https://it.trustpilot.com/review/dove.it',
          dataRef: 'cta-leave-review',
          dataTracking: 'cta-leave-review',
        },
      } : null,
      intro,
      teamSection: {
        title: team.title,
        content: realEstateAgency.team,
        agents: sortAgents(realEstateAgency.agents).map((agent) => ({
          id: agent.id,
          name: `${agent.name} ${agent.surname}`,
          jobTitle: agent.jobTitleType.name,
          picture: agent.photo,
          urlObject: agent.biography && buildAgentURLObject({ slug: agent.slug }),
          locality: realEstateAgency.parent.locality.name,
          franchising: realEstateAgency.franchising,
        })),
      },
      form,
    } as RealEstateAgencyProps,
  };
});

export default withError(withNextConfig(RealEstateAgency));
